import { useComponentInViewport } from 'core-lib/hooks/useComponentInViewport';
import useTimer from 'core-lib/hooks/useTimer';
import { useEffect, useRef, useState } from 'react';
import useStoreItemClick from 'src/modules/parkplusStoreV2/hooks/useStoreItemClick';
import {
  trackCtaClickedStoreV2,
  trackProductViewedStoreV2,
  trackSectionViewedStoreV2,
} from 'src/modules/parkplusStoreV2/utils/events';
import { Typography } from 'ui-v2/components/Typography';
import { Button } from 'ui-v2/components/Button';
import { LayoutEntityType, LayoutPropType } from '../../../types/layout';
import WeekendCard from '../../Cards/WeekendCard';
import s from './FlashListing.module.scss';
import Icon from 'ui-v2/components/Icon/Icon';
import dynamic from 'next/dynamic';

const Swiper = dynamic(() => import('core-lib/components/Swiper'), {
  ssr: false,
});

export const FlashListing = ({ data }: LayoutPropType) => {
  const { handleItemClickWithDelegation, handleItemClick } =
    useStoreItemClick();

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const CARDID = 'FLASH_LISTING';
  //for event
  const rootRef = useRef();
  const isEventFired = useRef<boolean>(false);
  const inViewPort = useComponentInViewport(rootRef, {
    threshold: 0.5,
  });

  const swiperRef = useRef({});

  useEffect(() => {
    if (inViewPort) {
      if (!isEventFired.current) {
        trackSectionViewedStoreV2(data?.title);
        isEventFired.current = true;
      }
      const product = data?.data?.[activeIndex];
      if (product && !swiperRef.current[product?.productId]) {
        trackProductViewedStoreV2('flash_sale_listing', product?.productId, {
          description: product?.eventType,
        });
        swiperRef.current[product?.productId] = true;
      }
    }
  }, [data, activeIndex, inViewPort]);

  const {
    hours: hr,
    minutes: min,
    seconds: sec,
  } = useTimer({
    expiryTimestamp: new Date(data?.endTime),
    onExpire: () => { },
  });

  return (
    <>
      <div
        ref={rootRef}
        className={s.root}
        style={{
          backgroundImage: `url(${data?.backgroundImage})`,
        }}
        onClick={(e) =>
          handleItemClickWithDelegation(
            e,
            CARDID,
            data?.entityType,
            data?.data,
            data?.title
          )
        }
      >
        {data?.endTime && (
          <div className={s.timer}>
            <div className={s.textStack}>
              <Typography
                label={`Ends in ${hr < 10 ? `0${hr}h` : `${hr}h`} : ${min < 10 ? `0${min}m` : `${min}m`
                  } : ${sec < 10 ? `0${sec}s` : `${sec}s`}`}
                variant={'body_3_medium'}
                color="on_dark_bg"
              />
            </div>
          </div>
        )}
        <div className={s.container}>
          <div className={s.carousel}>
            <Swiper
              onSlideChange={(el) => {
                setActiveIndex(el.realIndex);
              }}
              effect="creative"
              creativeEffect={{
                prev: {
                  translate: ['-100%', 0, 0],
                  scale: 0.9,
                },
                next: {
                  translate: ['100%', 0, 0],
                  scale: 0.9,
                },
              }}
              slidesPerView={1.4}
              centeredSlides
              spaceBetween={10}
              loop={true}
              slides={data?.data
                ?.filter((item) => item?.stock > 0)
                .map((item, id) => (
                  <div key={item?.productSku}>
                    <WeekendCard
                      product={item}
                      section={data?.title}
                      position={id}
                      id={CARDID}
                    />
                  </div>
                ))}
            />
          </div>
        </div>

        {data?.metaData?.categories?.[0] && (
          <div className={s.viewAll}>
            <Button
              variant="link_color"
              underline
              label="View all products"
              size="medium"
              rightIcon={
                <Icon name="ChevronRightRegular"
                  color='brand'
                  width={16}
                  height={16}
                />
              }
              style={{ width: 'calc(100% - 24px)', marginTop: `16px` }}
              onClick={() => {
                trackCtaClickedStoreV2(data?.title, 'view_all_products');
                const activeCat = data?.metaData?.categories?.[0];
                handleItemClick(
                  LayoutEntityType.Category,
                  activeCat?.productId,
                  activeCat?.filter
                );
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};
